import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import { messageService, conversationService } from '@services/api/messages'
import { bindChat, bindUserChannel } from '@services/pusher'
import { storageURL, mediaUrl } from '@config'
import useConfirm from '@hooks/useConfirm'

const mapConv = (c) => ({
  id:                c.id,
  contact_id:        c.other_client.client_id,
  contact_full_name: [c.other_client.nom, c.other_client.prenom].filter(Boolean).join(' ') || 'Inconnu',
  contact_avatar:    c.other_client.photo_profil ? mediaUrl(c.other_client.photo_profil) : null,
  contact_phone:     c.other_client.numero || '',
  last_message:      c.last_message?.urlArticle ? (c.last_message?.nomArticle || 'Article partagé') : (c.last_message?.message || ''),
  last_is_article:   !!c.last_message?.urlArticle,
  last_image:        c.last_message?.imageArticle || null,
  last_message_time: c.last_message?.created_at ?? c.updated_at,
  unread_count:      c.unread_count || 0,
})

function Avatar({ name, photo, size = 'md' }) {
  const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-12 h-12 text-sm' }
  const initials = name
    ? name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '?'
  return (
    <div className={`relative flex-shrink-0 ${sizes[size]}`}>
      {photo && (
        <img src={photo} alt={name} className="w-full h-full rounded-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
      )}
      <div
        className={`${photo ? 'hidden' : 'flex'} w-full h-full rounded-full bg-gradient-to-br from-[#FFE45C] to-[#F5B700] items-center justify-center text-[#002A4E] font-bold`}
        style={{ display: photo ? 'none' : 'flex' }}
      >
        {initials}
      </div>
    </div>
  )
}

function MessagesPage() {
  const { user } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const { confirm, ConfirmDialog } = useConfirm()
  const [conversations, setConversations] = useState([])
  const [activeConversation, setActiveConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sending, setSending] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showContactDetail, setShowContactDetail] = useState(false)
  const [menuOpenId, setMenuOpenId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState('')
  const [partnerTyping, setPartnerTyping] = useState(false)
  const lastTypingSentRef = useRef(0)
  const typingTimeoutRef = useRef(null)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })

  const loadConversations = async (silent = false) => {
    if (!user?.client_id) return
    try {
      if (!silent) setLoading(true)
      const response = await conversationService.getConversations()
      if (response.success) setConversations((response.data || []).map(mapConv))
    } catch (err) {
      console.error('Erreur conversations:', err)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const loadMessages = async (conversationId, silent = false) => {
    if (!user?.client_id) return
    try {
      if (!silent) setLoadingMessages(true)
      const response = await messageService.getMessages(conversationId)
      if (response.success) {
        const incoming = response.data || []
        setPartnerTyping(!!response.partner_typing)
        setMessages(prev => {
          if (incoming.length > prev.length || (prev.length === 0 && incoming.length > 0))
            setTimeout(scrollToBottom, 100)
          return incoming
        })
      }
    } catch (err) {
      console.error('Erreur messages:', err)
    } finally {
      if (!silent) setLoadingMessages(false)
    }
  }

  const createOrOpenConversation = async (contactId, contactInfo, article = null) => {
    if (!user?.client_id) return
    try {
      let conv = conversations.find(c => c.contact_id === contactId)
      if (!conv) {
        const res = await conversationService.createConversation(contactId)
        if (!res.success) return
        conv = mapConv(res.data)
        setConversations(prev => [conv, ...prev])
      }
      setActiveConversation(conv)
      await loadMessages(conv.id)

      if (article?.url) {
        const msgRes = await messageService.sendMessage({
          id_receiver:  contactId,
          urlArticle:   article.url,
          nomArticle:   article.name || '',
          imageArticle: article.image || '',
        })
        if (msgRes.success) {
          setMessages(prev => [...prev, msgRes.data])
          setConversations(prev => prev.map(c => c.id === conv.id
            ? { ...c, last_message: '📎 Article partagé', last_message_time: msgRes.data.created_at }
            : c
          ))
          setTimeout(scrollToBottom, 100)
        }
      }
    } catch (err) {
      console.error('Erreur conversation:', err)
    }
  }

  // Liste des conversations : chargée une fois, puis rafraîchie en temps réel
  // via le canal user-{id} (event 'conversation-updated' à chaque nouveau message).
  useEffect(() => {
    if (!user?.client_id) return
    loadConversations()
    const cleanup = bindUserChannel(user.client_id, {
      onConversationUpdated: () => loadConversations(true),
    })
    return cleanup
  }, [user?.client_id])

  useEffect(() => {
    if (loading) return
    if (!location.state?.openConversationWith) return
    const { id, article, ...contactInfo } = location.state.openConversationWith
    if (!id) return
    createOrOpenConversation(id, contactInfo, article)
    navigate(location.pathname, { replace: true, state: {} })
  }, [loading])

  // Messages de la conversation active : reçus en temps réel via le canal chat-{id}.
  useEffect(() => {
    if (!activeConversation?.id) return
    const convId = activeConversation.id

    const cleanup = bindChat(convId, {
      onMessage: (data) => {
        // Mes propres messages sont déjà affichés en optimiste → ignorer
        if (String(data.from) === String(user?.client_id)) return
        const mapped = {
          id:           data.id,
          id_sender:    data.from,
          id_receiver:  user?.client_id,
          message:      data.content || '',
          urlArticle:   data.image_article || null,
          nomArticle:   data.article_name || null,
          imageArticle: null,
          typeFichier:  data.type_fichier || null,
          created_at:   data.created_at,
          liked:        false,
          edited:       false,
        }
        setMessages(prev => {
          if (prev.some(m => m.id === mapped.id)) return prev // anti-doublon
          setTimeout(scrollToBottom, 100)
          return [...prev, mapped]
        })
        // Je regarde la conversation → marquer comme lu côté serveur
        messageService.markRead(convId).catch(() => {})
      },
      onTyping: (data) => {
        // Ignorer ma propre frappe ; afficher l'indicateur ~3 s
        if (String(data.sender_id) === String(user?.client_id)) return
        setPartnerTyping(true)
        clearTimeout(typingTimeoutRef.current)
        typingTimeoutRef.current = setTimeout(() => setPartnerTyping(false), 3000)
      },
    })

    return () => {
      cleanup()
      clearTimeout(typingTimeoutRef.current)
      setPartnerTyping(false)
    }
  }, [activeConversation?.id, user?.client_id])

  // Fermer le menu d'actions au clic extérieur
  useEffect(() => {
    if (!menuOpenId) return
    const close = () => setMenuOpenId(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [menuOpenId])

  const handleSelectConversation = async (conv) => {
    setActiveConversation(conv)
    setMessages([])
    setPartnerTyping(false)
    await loadMessages(conv.id)
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !activeConversation || !user) return
    try {
      setSending(true)
      const response = await messageService.sendMessage({
        id_receiver: activeConversation.contact_id,
        message:     newMessage.trim(),
      })
      if (response.success) {
        setMessages(prev => [...prev, response.data])
        setNewMessage('')
        setConversations(prev => prev.map(c => c.id === activeConversation.id
          ? { ...c, last_message: newMessage.trim(), last_message_time: response.data.created_at }
          : c
        ))
        setTimeout(scrollToBottom, 100)
      }
    } catch (err) {
      console.error('Erreur envoi:', err)
    } finally {
      setSending(false)
    }
  }

  // Signale la frappe (throttlé à 1 ping / 2,5s)
  const handleTyping = () => {
    if (!activeConversation) return
    const now = Date.now()
    if (now - lastTypingSentRef.current < 2500) return
    lastTypingSentRef.current = now
    messageService.sendTyping(activeConversation.id).catch(() => {})
  }

  const startEdit = (msg) => {
    setMenuOpenId(null)
    setEditingId(msg.id)
    setEditText(msg.message || '')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditText('')
  }

  const handleSaveEdit = async (msg) => {
    const text = editText.trim()
    if (!text || text === msg.message) { cancelEdit(); return }
    try {
      const response = await messageService.updateMessage(msg.id, text)
      if (response.success) {
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, message: text, edited: true } : m))
        setConversations(prev => prev.map(c => c.id === activeConversation.id && c.last_message_time === msg.created_at
          ? { ...c, last_message: text }
          : c
        ))
      }
    } catch (err) {
      console.error('Erreur modification:', err)
    } finally {
      cancelEdit()
    }
  }

  const handleToggleLike = async (msg) => {
    // Optimiste
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, liked: !m.liked } : m))
    try {
      const response = await messageService.toggleLike(msg.id)
      if (response.success) {
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, liked: response.liked } : m))
      }
    } catch (err) {
      console.error('Erreur like:', err)
      // Annuler en cas d'échec
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, liked: msg.liked } : m))
    }
  }

  const handleDeleteMessage = async (msg) => {
    setMenuOpenId(null)
    if (!(await confirm({ title: 'Supprimer le message', message: 'Supprimer ce message ?', type: 'danger', confirmText: 'Supprimer' }))) return
    try {
      const response = await messageService.deleteMessage(msg.id)
      if (response.success) {
        setMessages(prev => prev.filter(m => m.id !== msg.id))
      }
    } catch (err) {
      console.error('Erreur suppression:', err)
    }
  }

  const filteredConversations = conversations.filter(c =>
    c.contact_full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  )
  const totalUnread = conversations.reduce((s, c) => s + c.unread_count, 0)

  const formatTime = (d) => {
    if (!d) return ''
    const date = new Date(d)
    const diff = Date.now() - date
    const m = Math.floor(diff / 60000)
    const h = Math.floor(diff / 3600000)
    const dy = Math.floor(diff / 86400000)
    if (m < 1) return 'maintenant'
    if (m < 60) return `${m}min`
    if (h < 24) return `${h}h`
    if (dy < 7) return `${dy}j`
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
  }

  const formatMsgTime = (d) => {
    if (!d) return ''
    return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  }

  // Groupe les messages par date pour afficher un séparateur
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = new Date(msg.created_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
    if (!groups[date]) groups[date] = []
    groups[date].push(msg)
    return groups
  }, {})

  return (
    <div className="home-font bg-slate-50" style={{ height: 'calc(100vh - 80px)', overflow: 'hidden' }}>
      <div className="w-[94%] md:w-[80%] mx-auto py-6 flex flex-col" style={{ height: '100%' }}>

        <div className="flex-1 flex overflow-hidden rounded-md ring-1 ring-slate-200/70 min-h-0">

        {/* ═══ SIDEBAR CONVERSATIONS ═══ */}
        <div className={`${activeConversation ? 'hidden md:flex' : 'flex'} w-full md:w-[340px] lg:w-[380px] flex-shrink-0 flex-col bg-white border-r border-gray-200 overflow-hidden`}>
          {/* Header sidebar */}
          <div className="px-4 pt-5 pb-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h1 className="flex items-center gap-2.5 text-xl font-extrabold text-[#002A4E]"><span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#FFD700] text-[#002A4E]"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" /></svg></span>Messages</h1>
              {totalUnread > 0 && (
                <span className="bg-[#FFD700] text-[#002A4E] text-xs font-bold px-2 py-0.5 rounded-md">
                  {totalUnread}
                </span>
              )}
            </div>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-9 pr-4 py-2 bg-slate-100 rounded-md text-sm focus:outline-none focus:ring-4 focus:ring-amber-100 focus:bg-white transition-all"
              />
            </div>
          </div>

          {/* Liste conversations */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1,2,3,4].map(i => (
                  <div key={i} className="flex items-center gap-3 animate-pulse">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 bg-gray-200 rounded-full w-1/2" />
                      <div className="h-3 bg-gray-200 rounded-full w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-gray-400">
                <svg className="w-14 h-14 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-sm font-medium text-gray-500">Aucune conversation</p>
                <p className="text-xs text-gray-400 mt-1">Commencez depuis un article</p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                    activeConversation?.id === conv.id
                      ? 'bg-[#FFF8D6] border-l-[3px] border-[#F5B700]'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <Avatar name={conv.contact_full_name} photo={conv.contact_avatar} size="lg" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold text-[#002A4E]' : 'font-medium text-gray-800'}`}>
                        {conv.contact_full_name}
                      </span>
                      <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">{formatTime(conv.last_message_time)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5 gap-2">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {/* Miniature article ou icône image */}
                        {conv.last_is_article && (
                          conv.last_image ? (
                            <img
                              src={conv.last_image}
                              alt=""
                              className="w-8 h-8 rounded object-cover flex-shrink-0 border border-gray-200"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling?.classList.remove('hidden') }}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )
                        )}
                        <p className={`text-xs truncate ${conv.unread_count > 0 ? 'text-[#002A4E] font-medium' : 'text-gray-500'}`}>
                          {conv.last_message || <span className="italic text-gray-400">Aucun message</span>}
                        </p>
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="flex-shrink-0 w-5 h-5 bg-[#FFD700] text-[#002A4E] text-[10px] font-bold rounded-full flex items-center justify-center">
                          {conv.unread_count > 9 ? '9+' : conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ═══ ZONE CHAT ═══ */}
        <div className={`${activeConversation ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-[#f0f2f5] min-w-0`}>
          {activeConversation ? (
            <>
              {/* Header chat */}
              <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shadow-sm">
                <button
                  onClick={() => setActiveConversation(null)}
                  className="md:hidden p-1.5 hover:bg-gray-100 rounded-full transition-colors -ml-1"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <Avatar name={activeConversation.contact_full_name} photo={activeConversation.contact_avatar} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#002A4E] text-sm truncate">{activeConversation.contact_full_name}</p>
                  <p className="text-xs text-gray-400">{activeConversation.contact_phone || ''}</p>
                </div>
                {/* Bouton détail contact */}
                <button
                  onClick={() => setShowContactDetail(v => !v)}
                  className={`p-2 rounded-full transition-colors flex-shrink-0 ${showContactDetail ? 'bg-[#FFF8D6] text-[#B8860B]' : 'hover:bg-gray-100 text-gray-500'}`}
                  title="Détails du contact"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>

              {/* Contenu : messages + panneau détail */}
              <div className="flex-1 flex overflow-hidden min-h-0">

              {/* Zone messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin w-7 h-7 border-2 border-[#F5B700] border-t-transparent rounded-full" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md mb-4">
                      <svg className="w-8 h-8 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-gray-600">Démarrez la conversation</p>
                    <p className="text-xs text-gray-400 mt-1">Envoyez un premier message à {activeConversation.contact_full_name}</p>
                  </div>
                ) : (
                  Object.entries(groupedMessages).map(([date, msgs]) => (
                    <div key={date}>
                      {/* Séparateur de date */}
                      <div className="flex items-center gap-3 my-4">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-[10px] font-medium text-gray-400 bg-[#f0f2f5] px-2 capitalize">{date}</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>

                      {msgs.map((msg, idx) => {
                        const isMine = msg.id_sender === user?.client_id
                        const isLast = idx === msgs.length - 1 || msgs[idx + 1]?.id_sender !== msg.id_sender

                        return (
                          <div key={msg.id} className={`flex items-end gap-2 mb-1 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                            {/* Avatar — uniquement pour le dernier message d'une série */}
                            <div className="w-8 flex-shrink-0 mb-1">
                              {!isMine && isLast && (
                                <Avatar name={activeConversation.contact_full_name} photo={activeConversation.contact_avatar} size="sm" />
                              )}
                            </div>

                            <div className={`group relative max-w-[70%] flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                              {msg.urlArticle ? (
                                /* ── Carte Article ── */
                                <div
                                  onClick={() => navigate(msg.urlArticle)}
                                  className={`w-60 rounded-md overflow-hidden cursor-pointer hover:brightness-95 transition-all shadow-sm ${
                                    isMine ? 'rounded-tr-sm' : 'rounded-tl-sm'
                                  }`}
                                >
                                  {msg.imageArticle ? (
                                    <img
                                      src={msg.imageArticle}
                                      alt={msg.nomArticle}
                                      className="w-full h-40 object-cover"
                                      onError={(e) => { e.target.parentElement.querySelector('.fallback-img')?.classList.remove('hidden') ; e.target.remove() }}
                                    />
                                  ) : (
                                    <div className="w-full h-28 bg-gray-200 flex items-center justify-center">
                                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    </div>
                                  )}
                                  <div className={`px-3 py-2.5 ${isMine ? 'bg-[#FFD700]' : 'bg-white'}`}>
                                    <p className={`text-sm font-semibold truncate ${isMine ? 'text-[#002A4E]' : 'text-[#002A4E]'}`}>
                                      {msg.nomArticle || 'Voir l\'annonce'}
                                    </p>
                                    <p className={`text-xs mt-0.5 flex items-center gap-1 ${isMine ? 'text-[#002A4E]/70' : 'text-[#B8860B]'}`}>
                                      Voir l'annonce
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </p>
                                  </div>
                                </div>
                              ) : editingId === msg.id ? (
                                /* ── Mode édition ── */
                                <div className="flex flex-col gap-1.5 w-full min-w-[200px]">
                                  <textarea
                                    autoFocus
                                    value={editText}
                                    onChange={(e) => setEditText(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(msg) }
                                      if (e.key === 'Escape') cancelEdit()
                                    }}
                                    rows={2}
                                    className="w-full px-3 py-2 text-sm rounded-md border border-amber-300 focus:outline-none focus:ring-4 focus:ring-amber-100 resize-none text-gray-800"
                                  />
                                  <div className="flex items-center gap-2 justify-end">
                                    <button onClick={cancelEdit} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">Annuler</button>
                                    <button onClick={() => handleSaveEdit(msg)} className="text-xs font-semibold text-[#002A4E] bg-[#FFD700] hover:bg-[#F5C800] px-3 py-1 rounded-md">Enregistrer</button>
                                  </div>
                                </div>
                              ) : (
                                /* ── Bulle texte ── */
                                <div className="flex items-center gap-1.5">
                                  {/* Actions au survol */}
                                  <div className={`flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${isMine ? 'order-first' : 'order-last'}`}>
                                    {/* Bouton like (tous les messages) */}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleToggleLike(msg) }}
                                      className={`p-1 rounded-full hover:bg-gray-200 ${msg.liked ? 'text-red-500' : 'text-gray-400'}`}
                                      title="J'aime"
                                    >
                                      <svg className="w-4 h-4" fill={msg.liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                      </svg>
                                    </button>
                                    {/* Bouton menu (mes messages uniquement) */}
                                    {isMine && (
                                      <div className="relative">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === msg.id ? null : msg.id) }}
                                          className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                                        >
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 8a2 2 0 100-4 2 2 0 000 4zm0 6a2 2 0 100-4 2 2 0 000 4zm0 6a2 2 0 100-4 2 2 0 000 4z" />
                                          </svg>
                                        </button>
                                        {menuOpenId === msg.id && (
                                          <div className="absolute right-0 bottom-full mb-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-20">
                                            <button onClick={() => startEdit(msg)} className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                              Modifier
                                            </button>
                                            <button onClick={() => handleDeleteMessage(msg)} className="w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                              Supprimer
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {/* Bulle (double-clic = j'aime) */}
                                  <div
                                    onDoubleClick={() => handleToggleLike(msg)}
                                    className={`relative px-3.5 py-2 shadow-sm max-w-full select-none ${
                                      isMine
                                        ? 'bg-[#FFD700] text-[#002A4E] rounded-md rounded-tr-none'
                                        : 'bg-white text-gray-800 rounded-md rounded-tl-none'
                                    }`}
                                  >
                                    <p className="text-sm leading-relaxed break-words">{msg.message}</p>
                                    {msg.liked && (
                                      <span className={`absolute -bottom-2 ${isMine ? '-left-1' : '-right-1'} w-5 h-5 bg-white rounded-full shadow flex items-center justify-center border border-gray-100`}>
                                        <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                                        </svg>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {/* Heure */}
                              {editingId !== msg.id && (
                                <span className="text-[10px] text-gray-400 mt-1 px-1">
                                  {formatMsgTime(msg.created_at)}
                                  {msg.edited && <span className="italic"> · modifié</span>}
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ))
                )}
                {/* ── Indicateur "en train d'écrire" ── */}
                {partnerTyping && (
                  <div className="flex items-end gap-2 mb-1">
                    <div className="w-8 flex-shrink-0 mb-1">
                      <Avatar name={activeConversation.contact_full_name} photo={activeConversation.contact_avatar} size="sm" />
                    </div>
                    <div className="bg-white rounded-md rounded-tl-none px-4 py-3 shadow-sm flex items-center gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* ── Panneau détail contact ── */}
              {showContactDetail && (
                <div className="w-72 flex-shrink-0 bg-white border-l border-gray-200 overflow-y-auto flex flex-col">
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <span className="font-semibold text-sm text-[#002A4E]">Détails</span>
                    <button onClick={() => setShowContactDetail(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {/* Avatar + nom */}
                  <div className="flex flex-col items-center pt-6 pb-4 px-4 border-b border-gray-100">
                    <Avatar name={activeConversation.contact_full_name} photo={activeConversation.contact_avatar} size="lg" />
                    <p className="mt-3 font-bold text-[#002A4E] text-base text-center">{activeConversation.contact_full_name}</p>
                    {activeConversation.contact_phone && (
                      <p className="text-xs text-gray-500 mt-1">{activeConversation.contact_phone}</p>
                    )}
                  </div>
                  {/* Infos */}
                  <div className="p-4 space-y-3">
                    {activeConversation.contact_phone && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Téléphone</p>
                          <p className="text-sm font-medium text-gray-800">{activeConversation.contact_phone}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide">Messages</p>
                        <p className="text-sm font-medium text-gray-800">{messages.length} message{messages.length > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              </div>{/* fin flex-1 flex contenu */}

              {/* ── Zone de saisie ── */}
              <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-3">
                <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => { setNewMessage(e.target.value); handleTyping() }}
                    placeholder="Écrivez un message..."
                    disabled={sending}
                    className="flex-1 px-4 py-2.5 bg-slate-100 rounded-md text-sm focus:outline-none focus:ring-4 focus:ring-amber-100 focus:bg-white transition-all disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="flex-shrink-0 w-10 h-10 bg-[#FFD700] text-[#002A4E] rounded-md flex items-center justify-center hover:bg-[#F5C800] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                  >
                    {sending ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-4 h-4 translate-x-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    )}
                  </button>
                </form>
              </div>
            </>
          ) : (
            /* ── État vide (desktop) ── */
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-md mb-6">
                <svg className="w-12 h-12 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-700 mb-2">Vos messages</h2>
              <p className="text-sm text-gray-400 max-w-xs">
                Sélectionnez une conversation ou envoyez un message depuis un article
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
    {ConfirmDialog}
  </div>
  )
}

export default MessagesPage
